import { Component, effect, input, model, signal } from '@angular/core';

@Component({
  selector: 'app-email-tag-input',
  templateUrl: './email-tag-input.html',
})
export class EmailTagInput {
  tags = model<string[]>([]);
  inputValue = signal<string>('');
  errorMessage = model<string>('');
  isRequired = input<boolean>(false);
  id = input<string>('tag-input');

  private hasBeenFocused = false;
  private readonly EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  constructor() {
    effect(() => {
      if (this.isRequired()) {
        this.checkRequired();
      }
    });
  }

  onBlur(): void {
    this.hasBeenFocused = true;
    this.validateRequired();
  }

  onFocus(): void {
    this.hasBeenFocused = true;
  }

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.inputValue.set(input.value);
    this.clearError();
  }

  addTag(): void {
    const value = this.inputValue().trim();
    if (!value) {
      this.errorMessage.set('Email cannot be empty');
      return;
    }

    if (!this.isValidEmail(value)) {
      this.errorMessage.set('Please enter a valid email address');
      return;
    }

    if (this.tags().includes(value)) {
      this.errorMessage.set('This email is already added');
      return;
    }

    // Create new array instead of mutating
    this.tags.set([...this.tags(), value]);
    this.inputValue.set('');
    this.clearError();
  }

  removeTag(index: number): void {
    // Create new array without the item at the specified index
    const currentTags = this.tags();
    const newTags = currentTags.filter((_, i) => i !== index);
    this.tags.set(newTags);

    if (this.hasBeenFocused) {
      this.checkRequired();
    }
  }

  removeTagByBackspace(): void {
    if (!this.inputValue().trim() && this.tags().length > 0) {
      this.removeTag(this.tags().length - 1);
    }
  }

  resetTags(): void {
    this.tags.set([]);
    this.inputValue.set('');
    this.hasBeenFocused = false;
    this.clearError();
  }

  validateRequired(): boolean {
    if (this.isRequired() && this.tags().length === 0) {
      this.errorMessage.set('At least one email is required');
      return false;
    }
    this.clearError(); // Clear error if validation passes
    return true;
  }

  private checkRequired(): void {
    if (this.hasBeenFocused && this.isRequired() && this.tags().length === 0) {
      this.errorMessage.set('At least one email is required');
    } else if (
      this.tags().length > 0 &&
      this.errorMessage() === 'At least one email is required'
    ) {
      // Only clear the "required" error, not validation errors
      this.clearError();
    }
  }

  private clearError(): void {
    if (this.errorMessage()) {
      this.errorMessage.set('');
    }
  }

  private isValidEmail(email: string): boolean {
    return this.EMAIL_REGEX.test(email);
  }
}
